import { useMemo } from 'react';
import { useQuery } from '@common-fe/common-fe';
import { AccountStatus, CardTypesEnum } from '@common-fe/common-fe';
import { toString } from 'lodash';

import { api } from '@/api';
import { API_URL, IAccountVerificationStatusResponseDto } from '@/dtos';

const PENDING_MANUAL = 'PENDING_MANUAL';

interface AccountVerificationStatus {
  id?: string;
  accountId?: string;
  status?: string;
  validFrom?: string;
  validTo?: string;
}

export interface AccountWithVerificationStatus {
  id?: string;
  description?: string;
  externalIdentifier?: string;
  availableBalance?: number;
  onDepositBalance?: number;
  liveBalance?: number;
  parentAccountId?: string;
  createdAt?: string;
  modifiedAt?: string;
  accountType?: string;
  accountCategory?: string;
  routingTransitNumber?: string;
  organizationId?: string;
  organizationPath?: string;
  numberLength?: string;
  leafNode?: boolean;
  employeeId?: string;
  accountStatus?: AccountVerificationStatus;
  accountNumberLast4?: string;
  verificationStatus?: string;
}

const formatData = (data?: IAccountVerificationStatusResponseDto) => {
  if (!data) return undefined;

  return {
    id: toString(data?.id),
    description: data?.description,
    externalIdentifier: toString(data?.external_identifier),
    availableBalance: data?.available_balance,
    onDepositBalance: data?.on_deposit_balance,
    liveBalance: data?.live_balance,
    parentAccountId: toString(data?.parent_account_id),
    createdAt: data?.created_at,
    modifiedAt: data?.modified_at,
    accountType: data?.account_type,
    accountCategory: data?.account_category,
    routingTransitNumber: data?.routing_transit_number,
    organizationId: toString(data?.organization_id),
    organizationPath: data?.organization_path,
    numberLength: data?.number_length,
    leafNode: data?.leaf_node,
    employeeId: toString(data?.employee_id),
    ...(data?.account_status
      ? {
          accountStatus: {
            id: toString(data.account_status?.id),
            accountId: toString(data.account_status?.account_id),
            status: data.account_status?.status,
            validFrom: data.account_status?.valid_from,
            validTo: data.account_status?.valid_to,
          },
        }
      : {}),
    accountNumberLast4: data?.account_number_last4,
    verificationStatus: data?.verification_status,
  } as AccountWithVerificationStatus;
};

const useGetAccountsWithVerificationStatusQuery = (
  employeeId?: string,
  accountType?: CardTypesEnum,
  accountStatus?: AccountStatus
) => {
  const { data, isLoading, isSuccess, refetch } = useQuery(
    [API_URL.ACCOUNTS_VERIFICATION, accountType, accountStatus, employeeId],
    () =>
      api.get(API_URL.ACCOUNTS_VERIFICATION, {
        params: {
          employee_id: employeeId,
          account_type: accountType,
          account_status_types: accountStatus,
        },
      }),
    {
      enabled: Boolean(employeeId && accountType && accountStatus),
    }
  );

  const formattedAccount = useMemo(() => {
    if (data?.data && data.data?.length) {
      const withPendingManualStatus = data.data.find((item) => item.verification_status === PENDING_MANUAL);
      return formatData(withPendingManualStatus);
    }
  }, [data]);

  const formattedData = useMemo(() => {
    if (data?.data && data.data?.length) {
      return data.data.map((item) => formatData(item));
    }
    return [];
  }, [data]);

  return {
    formattedData,
    formattedAccount,
    isLoading,
    isSuccess,
    refetch,
  };
};

export default useGetAccountsWithVerificationStatusQuery;
